import { ReactNode } from "react"

import { PopoverComboboxItem } from "../types"

export interface CustomItem {
  key: string
  label?: string
  onSelect: () => void
  content?: ReactNode
  accessory?: ReactNode
}

export function isCustomItem<T>(item: T | CustomItem): item is CustomItem {
  return (
    (item as CustomItem).key !== undefined &&
    ((item as CustomItem).label !== undefined ||
      (item as CustomItem).content !== undefined) &&
    (item as CustomItem).onSelect !== undefined
  )
}

export interface PopoverSelectProps<
  T extends PopoverComboboxItem = PopoverComboboxItem,
> {
  items: T[]
  selected: T | null
  onSelect: (item: T | null) => void
  onChangeQuery: (query: string) => void
  label?: string
  placeholder: string
  disabled?: boolean
  invalid?: boolean
  errorMessage?: string
  className?: string
  // Applied to the trigger button; use to override the default bordered shell
  // (e.g. a borderless inline trigger).
  buttonClassName?: string
  buttonSize?: "small" | "large"

  // Optional accessible name for the trigger button
  buttonAriaLabel?: string
  buttonTitleOverride?: string

  // Hides the trailing chevron on the trigger button. Defaults to shown.
  hideChevron?: boolean

  // Caps the number of characters accepted in the search input.
  maxLength?: number
}

export type PopoverSelectComboboxProps<
  T extends PopoverComboboxItem = PopoverComboboxItem,
> = Pick<
  PopoverSelectProps<T>,
  "items" | "selected" | "onSelect" | "onChangeQuery" | "maxLength"
>

export type AccessoryContext = "button" | "list"

export interface UsePopoverSelectProps<T>
  extends Pick<PopoverSelectProps, "onChangeQuery"> {
  items: T[]
  selected?: T | null
  defaultSelected?: T
  onSelect?: (item: T | null) => void
  keyExtractor: (item: T) => string
  labelExtractor: (item: T) => string
  // If defined, content is used instead of the label and accessory. The context
  // distinguishes the trigger button from the dropdown list items so a caller can
  // render them differently (e.g. a one-line trigger vs a two-line list row).
  contentExtractor?: (item: T, context: AccessoryContext) => ReactNode
  accessoryExtractor?: (item: T, context: AccessoryContext) => ReactNode
  customItems?: CustomItem[]
  customItemsPlacement?: "start" | "end"

  /**
   * Allows selecting a non-T option (e.g. "Default unit") by key.
   * If provided and `selected` is null, the combobox selection will be set to
   * the matching combobox item (including custom items) so the UI can show it
   * as selected.
   */
  selectedKeyOverride?: string
  selectedLabelOverride?: string
}

export interface UsePopoverSelectRes<T> {
  popoverSelectProps: Pick<
    PopoverSelectProps,
    "items" | "selected" | "onSelect" | "onChangeQuery"
  >
  selected: T | null
}
